// The host's game options in the room: a ruleset picker and each game's own settings. Conquer the
// Flag's live in its own panel (CtfRoomOptions), since each player picks their own team there.
import React from 'react';
import { GAMES, getActiveRuleset } from '../../games/registry';
import { DEFAULT_CONFIG as CLASSIC_CARDS_DEFAULT_CONFIG } from '../../games/classic-cards/classic-cards';
import { DEFAULT_CONFIG as LODDEN_DEFAULT_CONFIG } from '../../games/lodden-thinks/lodden';
import { DEFAULT_CONFIG as BADUK_DEFAULT_CONFIG } from '../../games/baduk-analysis/baduk';
import { DEFAULT_CONFIG as HEIST_CITY_DEFAULT_CONFIG } from '../../games/heist-city/heist-city';
import { DEFAULT_CONFIG as KTD_DEFAULT_CONFIG } from '../../games/kill-team-draft/kill-team-draft';
import type { ClassicCardsConfig } from '../../games/classic-cards/classic-cards';
import type { LoddenConfig } from '../../games/lodden-thinks/lodden';
import type { BadukConfig } from '../../games/baduk-analysis/baduk';
import type { HeistCityConfig } from '../../games/heist-city/heist-city';
import type { KTDConfig } from '../../games/kill-team-draft/kill-team-draft';
import type { RoomView } from '../../types/room';

function RulesetPicker({ gameId, config, onChange }: {
  gameId: string;
  config: Record<string, unknown>;
  onChange: (rulesetId: string, rulesetConfig: Record<string, unknown>) => void;
}) {
  const game = GAMES.find(g => g.id === gameId);
  if (!game || game.rulesets.length <= 1) return null;
  const current = getActiveRuleset(gameId, config) ?? game.rulesets[0];
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      Ruleset:{' '}
      <select
        value={current.id}
        onChange={e => {
          const selected = game.rulesets.find(r => r.id === e.target.value);
          if (selected) onChange(selected.id, selected.config);
        }}
      >
        {game.rulesets.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
      </select>
      <span style={{ fontSize: '0.8em', color: '#888' }}>{current.description}</span>
    </label>
  );
}

function ClassicCardsConfigPanel({ config, onChange }: { config: ClassicCardsConfig; onChange: (c: ClassicCardsConfig) => void }) {
  const game = config.game ?? 'war';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {game === 'war' && (
        <>
          <label>
            Variant:{' '}
            <select value={config.warVariant ?? 'regular'} onChange={e => onChange({ ...config, warVariant: e.target.value as ClassicCardsConfig['warVariant'] })}>
              <option value="regular">Regular</option>
              <option value="aces-high">Aces High</option>
            </select>
          </label>
          <label>
            First to{' '}
            <input type="number" min={1} max={20} value={config.warWinningScore ?? 5}
              onChange={e => onChange({ ...config, warWinningScore: Math.max(1, parseInt(e.target.value, 10) || 5) })}
              style={{ width: 48 }} />{' '}points wins
          </label>
        </>
      )}
      {game === 'texas-holdem' && (
        <>
          <label>
            Starting chips:{' '}
            <input type="number" min={100} max={10000} step={100} value={config.holdemStartingChips ?? 1000}
              onChange={e => onChange({ ...config, holdemStartingChips: Math.max(100, parseInt(e.target.value, 10) || 1000) })}
              style={{ width: 72 }} />
          </label>
          <label>
            Small blind:{' '}
            <input type="number" min={1} max={500} value={config.holdemSmallBlind ?? 10}
              onChange={e => onChange({ ...config, holdemSmallBlind: Math.max(1, parseInt(e.target.value, 10) || 10) })}
              style={{ width: 60 }} />
          </label>
          <label>
            Big blind:{' '}
            <input type="number" min={2} max={1000} value={config.holdemBigBlind ?? 20}
              onChange={e => onChange({ ...config, holdemBigBlind: Math.max(2, parseInt(e.target.value, 10) || 20) })}
              style={{ width: 60 }} />
          </label>
        </>
      )}
      {game === 'gin-rummy' && (
        <label>
          Target score:{' '}
          <input type="number" min={50} max={500} step={50} value={config.ginTargetScore ?? 100}
            onChange={e => onChange({ ...config, ginTargetScore: Math.max(50, parseInt(e.target.value, 10) || 100) })}
            style={{ width: 60 }} />
        </label>
      )}
    </div>
  );
}

function LoddenConfigPanel({ config, onChange }: { config: LoddenConfig; onChange: (c: LoddenConfig) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <label>
        First to{' '}
        <input type="number" min={1} max={20} value={config.winningScore}
          onChange={e => onChange({ ...config, winningScore: Math.max(1, parseInt(e.target.value, 10) || 5) })}
          style={{ width: 48 }} />{' '}bettor wins wins the game
      </label>
    </div>
  );
}

function BadukConfigPanel({ config, onChange }: { config: BadukConfig; onChange: (c: BadukConfig) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <label>
        Board size:{' '}
        <select value={config.boardSize ?? 19} onChange={e => onChange({ ...config, boardSize: parseInt(e.target.value, 10) })}>
          <option value={9}>9×9</option>
          <option value={13}>13×13</option>
          <option value={19}>19×19</option>
        </select>
      </label>
      <label>
        Komi:{' '}
        <input type="number" min={0} max={13} step={0.5} value={config.komi}
          onChange={e => onChange({ ...config, komi: parseFloat(e.target.value) || 6.5 })}
          style={{ width: 64 }} />
      </label>
      <label>
        Handicap:{' '}
        <select value={config.handicap} onChange={e => onChange({ ...config, handicap: parseInt(e.target.value, 10) })}>
          {[0,2,3,4,5,6,7,8,9].map(n => <option key={n} value={n}>{n === 0 ? 'None' : n}</option>)}
        </select>
      </label>
    </div>
  );
}

const HEIST_CITY_MAPS = [
  { id: 'bank-job-hex', name: 'Bank Job (Hex)' },
  { id: 'ballroom', name: 'Ballroom' },
  { id: 'skyscrapper', name: 'Skyscraper' },
  { id: 'hex-demo', name: 'Hex Demo' },
];

function KTDConfigPanel({ config, onChange }: { config: KTDConfig; onChange: (c: KTDConfig) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <label>
        Cards per pack:{' '}
        <input type="number" min={5} max={30} value={config.packSize}
          onChange={e => onChange({ ...config, packSize: Math.max(5, parseInt(e.target.value, 10) || 15) })}
          style={{ width: 48 }} />
      </label>
      <label>
        Number of packs:{' '}
        <input type="number" min={1} max={5} value={config.totalPacks}
          onChange={e => onChange({ ...config, totalPacks: Math.max(1, parseInt(e.target.value, 10) || 3) })}
          style={{ width: 48 }} />
      </label>
    </div>
  );
}


function HeistCityConfigPanel({ config, onChange }: { config: HeistCityConfig; onChange: (c: HeistCityConfig) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <label>
        Map:{' '}
        <select value={config.mapId} onChange={e => onChange({ ...config, mapId: e.target.value })}>
          {HEIST_CITY_MAPS.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </label>
    </div>
  );
}

// The options panel for the room's game (host only). Returns null for games with nothing to set.
export default function GameOptions({ room, setConfig }: { room: RoomView; setConfig: (c: Record<string, unknown>) => void }) {
  const has = Object.keys(room.config).length > 0;
  const withRulesetId = (c: Record<string, unknown>) =>
    room.config.rulesetId !== undefined ? { ...c, rulesetId: room.config.rulesetId } : c;
  const put = (c: unknown) => setConfig(withRulesetId(c as Record<string, unknown>));
  const panel = (() => {
    switch (room.gameId) {
      case 'classic-cards': return <ClassicCardsConfigPanel config={has ? room.config as unknown as ClassicCardsConfig : CLASSIC_CARDS_DEFAULT_CONFIG} onChange={put} />;
      case 'lodden-thinks': return <LoddenConfigPanel config={has ? room.config as unknown as LoddenConfig : LODDEN_DEFAULT_CONFIG} onChange={put} />;
      case 'baduk-analysis': return <BadukConfigPanel config={has ? room.config as unknown as BadukConfig : BADUK_DEFAULT_CONFIG} onChange={put} />;
      case 'heist-city': return <HeistCityConfigPanel config={has ? room.config as unknown as HeistCityConfig : HEIST_CITY_DEFAULT_CONFIG} onChange={put} />;
      case 'kill-team-draft': return <KTDConfigPanel config={has ? room.config as unknown as KTDConfig : KTD_DEFAULT_CONFIG} onChange={put} />;
      default: return null;
    }
  })();
  const game = GAMES.find(g => g.id === room.gameId);
  if (!panel && !(game && game.rulesets.length > 1)) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <RulesetPicker gameId={room.gameId} config={room.config} onChange={(rulesetId, rulesetConfig) => setConfig({ ...rulesetConfig, rulesetId })} />
      {panel}
    </div>
  );
}

// The ruleset name for people who aren't the host.
export function rulesetName(room: RoomView): string | null {
  return getActiveRuleset(room.gameId, room.config)?.name ?? null;
}
